import { useEffect, useMemo, useRef, useState } from "react";

import { useAccounts } from "../api/hooks/useAccounts";
import { ImportQuotaExceededError, useCommitImport } from "../api/hooks/useCommitImport";
import { useImportJobReport } from "../api/hooks/useImportJobReport";
import {
  isImportJobActive,
  type ImportJob,
  useDeletedImportJobs,
  useImportJobs,
} from "../api/hooks/useImportJobs";
import { useImportUsage } from "../api/hooks/useImportUsage";
import { useRestoreImportBatch } from "../api/hooks/useRestoreImportBatch";
import {
  useRecordStatementConsent,
  useStatementConsent,
} from "../api/hooks/useStatementConsent";
import {
  AccountMaskConflictError,
  useRebindAccountMask,
} from "../api/hooks/useRebindAccountMask";
import {
  StatementTimezoneAlreadySetError,
  useSetStatementTimezone,
} from "../api/hooks/useSetStatementTimezone";
import { useStatementSample } from "../api/hooks/useStatementSample";
import { ImportDiagnosticsError, useValidateImport } from "../api/hooks/useValidateImport";
import { ImportDiagnosticsAlert } from "../components/imports/ImportDiagnosticsAlert";
import {
  StatementTimezonePicker,
  type StatementTimezoneConflict,
} from "../components/imports/StatementTimezonePicker";
import {
  hasBlockingDiagnostic,
  parseImportDiagnostics,
  REMEDY_REBIND_ACCOUNT_MASK,
  REMEDY_SET_STATEMENT_TIMEZONE,
  type ImportDiagnostic,
} from "../lib/importDiagnostics";
import type { components } from "../api/schema";
import { AccountFormDialog } from "../components/accounts/AccountFormDialog";
import { DeleteImportDialog } from "../components/imports/DeleteImportDialog";
import { FileDropzone } from "../components/imports/FileDropzone";
import { ImportGuidance } from "../components/imports/ImportGuidance";
import { ParsedRowsTable } from "../components/imports/ParsedRowsTable";
import { useImportSuccessBanner } from "../components/shell/ImportSuccessBanner";
import { Button } from "../components/ui/button";
import { useToast } from "../components/ui/toast";
import { formatDate } from "../lib/date";
import { readLastImportAccount, writeLastImportAccount } from "../lib/importAccount";

type PreviewImportResponse = components["schemas"]["PreviewImportResponse"];
type StatementSampleResponse = components["schemas"]["StatementSampleResponse"];
type AccountResponse = components["schemas"]["AccountResponse"];
type RowErrorResponse = components["schemas"]["RowErrorResponse"];
type DuplicateImportRef = components["schemas"]["DuplicateImportRef"];

// Graceful-import outcome surfaced once a background commit finishes (ADR 0034:
// fetched from GET /imports/jobs/{id}/report). Either the whole file was blocked
// (nothing imported) or some symbols were withheld while the rest imported. Two
// withhold causes are reported distinctly:
//   * symbols (L0-U7)   -- rows couldn't be PARSED; fix the rows and re-import.
//   * unreconciled      -- rows parsed but closes had no matching opens (usually
//                          opened before the exported range); export wider.
// Cleared when the operator picks another file.
type CommitReport =
  | { kind: "blocked"; rowErrors: RowErrorResponse[] }
  | {
      kind: "quarantine";
      symbols: string[];
      unreconciled: string[];
      rowErrors: RowErrorResponse[];
    };

const SELECT_CLASS =
  "flex h-9 w-full rounded-control border border-outline bg-surface-container px-3 py-1 text-sm text-on-surface shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring disabled:cursor-not-allowed disabled:opacity-50";

function formatExecutedAt(value: string | null): string {
  if (value === null) return "—";
  const date = new Date(value);
  return date.toLocaleString();
}

function describeCommitError(err: unknown): string {
  if (err instanceof ImportQuotaExceededError) return "Monthly import limit reached.";
  return err instanceof Error ? err.message : "Failed to commit import.";
}

// One-line outcome for the recent-imports list (job-backed, ADR 0023). Active
// jobs read as "Importing…"; terminal jobs show their counts or failure. A
// reaper-interrupted job (ADR 0034 STALE) reads distinctly so the user knows to
// re-import rather than treating it as a hard failure.
function describeJobOutcome(job: ImportJob): string {
  if (job.status === "running" || job.status === "pending") return "Importing…";
  if (job.status === "stale") return "Interrupted — please re-import";
  if (job.status === "failed") return job.error_message ?? "Failed";
  return `${job.fills_imported ?? 0} fill(s), ${job.trades_created ?? 0} trade(s)`;
}

function CommitReportAlert({ report }: { report: CommitReport }) {
  if (report.kind === "blocked") {
    return (
      <div
        role="alert"
        data-testid="import-blocked"
        className="space-y-2 rounded-control border border-danger bg-danger/10 p-4 text-sm text-on-surface"
      >
        <strong>Import blocked &mdash; nothing was imported.</strong>
        <p className="text-on-surface-variant">
          {report.rowErrors.length} row(s) couldn&rsquo;t be read, and we couldn&rsquo;t tell
          which symbol they belong to. Fix those rows in your statement and upload again.
        </p>
        {report.rowErrors.length > 0 && (
          <ul className="list-disc space-y-1 pl-5 text-xs text-on-surface-variant">
            {report.rowErrors.map((err) => (
              <li key={`${err.row_number}-${err.message}`}>{err.message}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }
  return (
    <div
      role="status"
      data-testid="import-quarantine"
      className="space-y-2 rounded-control border border-warning bg-warning/10 p-4 text-sm text-on-surface"
    >
      {report.symbols.length > 0 && (
        <div className="space-y-1">
          <strong>Imported &mdash; {report.symbols.length} symbol(s) skipped.</strong>
          <p className="text-on-surface-variant">
            Some rows couldn&rsquo;t be read for {report.symbols.join(", ")}. Those symbols were
            skipped so your trade quantities stay correct; fix them and re-import.
          </p>
        </div>
      )}
      {report.unreconciled.length > 0 && (
        <div className="space-y-1" data-testid="import-unreconciled">
          <strong>
            Imported &mdash; {report.unreconciled.length} symbol(s) couldn&rsquo;t be matched.
          </strong>
          <p className="text-on-surface-variant">
            We couldn&rsquo;t match opening and closing trades for{" "}
            {report.unreconciled.join(", ")}. This usually means the position was opened before
            this statement&rsquo;s date range, so the closing trades have no matching buys. Export
            a wider date range that includes the opening trades, then re-import &mdash; every other
            symbol was imported.
          </p>
        </div>
      )}
      {report.rowErrors.length > 0 && (
        <ul className="list-disc space-y-1 pl-5 text-xs text-on-surface-variant">
          {report.rowErrors.map((err) => (
            <li key={`${err.row_number}-${err.message}`}>{err.message}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Duplicate-import advisory (L0-U25, ADR 0035). /imports/validate returns
// duplicate_of when this exact file (by sha256) was already imported by a prior
// SUCCEEDED + ACTIVE job, matched tenant-wide. The match is ADVISORY ONLY -- the
// commit is never blocked -- so this is a dismissible warning, not a gate:
// Confirm import stays enabled throughout. Because the match is tenant-wide, the
// prior import may have targeted a DIFFERENT account than the one selected now
// (the common wrong-account mistake), so we call that out explicitly.
function DuplicateImportWarning({
  duplicate,
  priorAccountName,
  accountMismatch,
  onDismiss,
}: {
  duplicate: DuplicateImportRef;
  priorAccountName: string;
  accountMismatch: boolean;
  onDismiss: () => void;
}) {
  return (
    <div
      role="status"
      data-testid="import-duplicate-warning"
      className="flex items-start justify-between gap-3 rounded-control border border-warning bg-warning/10 p-4 text-sm text-on-surface"
    >
      <div className="space-y-1">
        <strong>This file was already imported.</strong>
        <p className="text-on-surface-variant">
          A file with the same contents was imported on {formatDate(duplicate.imported_at)} to{" "}
          {priorAccountName}.{" "}
          {accountMismatch && (
            <span data-testid="import-duplicate-mismatch" className="text-warning">
              That&rsquo;s a different account than the one selected now &mdash; double-check
              you&rsquo;re importing into the right account.{" "}
            </span>
          )}
          Importing again skips any duplicate fills, so it&rsquo;s safe to proceed anyway.
        </p>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="shrink-0"
        aria-label="Dismiss duplicate warning"
        onClick={onDismiss}
      >
        Dismiss
      </Button>
    </div>
  );
}

// Statement-collection consent gate (L0-U13, ADR 0028 D4). Shown in place of the
// upload controls until the user acknowledges how their broker statement is
// stored and used. The acknowledgement is recorded server-side so it carries
// across devices; the import controls reappear once it is on record.
function StatementConsentGate({
  onAgree,
  pending,
  failed,
}: {
  onAgree: () => void;
  pending: boolean;
  failed: boolean;
}) {
  return (
    <div
      data-testid="statement-consent-gate"
      role="region"
      aria-label="Statement collection consent"
      className="space-y-3 rounded-control border border-outline bg-surface-container p-4 shadow-sm"
    >
      <h2 className="text-sm font-medium text-on-surface">Before you upload a statement</h2>
      <p className="text-sm text-on-surface-variant">
        Your broker statement is uploaded to build your private trading journal. It is stored only
        within your own account, used solely to show your data back to you, and is never sold or
        shared with other users. WickScope is a record-keeping tool, not financial advice &mdash;
        see the{" "}
        <a href="/disclaimer" target="_blank" rel="noreferrer" className="text-primary underline">
          financial disclaimer
        </a>{" "}
        and{" "}
        <a href="/privacy" target="_blank" rel="noreferrer" className="text-primary underline">
          privacy policy
        </a>
        .
      </p>
      <Button type="button" onClick={onAgree} disabled={pending}>
        {pending ? "Saving…" : "I understand and agree"}
      </Button>
      {failed ? (
        <p role="alert" className="text-sm text-danger">
          Could not save your acknowledgement. Please try again.
        </p>
      ) : null}
    </div>
  );
}

export function ImportsNewPage() {
  const { showToast } = useToast();
  const banner = useImportSuccessBanner();

  const accountsQuery = useAccounts();
  const usageQuery = useImportUsage();
  const importJobsQuery = useImportJobs();
  const deletedJobsQuery = useDeletedImportJobs();
  const restoreMutation = useRestoreImportBatch();
  const validateMutation = useValidateImport();
  const sampleMutation = useStatementSample();
  const setTimezoneMutation = useSetStatementTimezone();
  const rebindMutation = useRebindAccountMask();
  const commitMutation = useCommitImport();
  const consentQuery = useStatementConsent();
  const recordConsent = useRecordStatementConsent();

  // Fail open (the AcceptanceGate precedent): only replace the upload controls
  // once the status query has definitively reported the consent is not on
  // record. While it loads or on a transient failure the controls show as
  // before -- the server is the real boundary and nothing is uploaded here.
  const consentNotGiven = consentQuery.data?.accepted === false;

  const [files, setFiles] = useState<File[]>([]);
  const [broker, setBroker] = useState<string>("tos");
  const [accountId, setAccountId] = useState<string>("");
  // Separate input: the re-link needs the statement again when the page was
  // reloaded between the refusal and the fix.
  const rebindFileInputRef = useRef<HTMLInputElement | null>(null);
  const [preview, setPreview] = useState<PreviewImportResponse | null>(null);
  const [diagnostics, setDiagnostics] = useState<ImportDiagnostic[]>([]);
  // ADR 0039 D9.2, the zone picker. `statementSample` is non-null only while the
  // picker is on screen: it is fetched when validate refuses for a missing zone
  // and the statement is still in hand. The two companions are the picker's
  // outcome state -- the 409's in-force zone (read off the response body, never
  // a re-fetch) and a field-level message for a 422 / 404 / transport failure.
  const [statementSample, setStatementSample] = useState<StatementSampleResponse | null>(null);
  const [zoneConflict, setZoneConflict] = useState<StatementTimezoneConflict | null>(null);
  const [zoneError, setZoneError] = useState<string | null>(null);
  // Which (account, file) pair the sample has already been requested for, so the
  // up-front offer below fires ONCE per pair rather than on every render. A ref
  // rather than state: it must not itself trigger a render, or the guard becomes
  // the loop it exists to prevent.
  const sampleRequestedForRef = useRef<string | null>(null);
  // L0-U25: the duplicate-import advisory is dismissible per preview. Dismissal
  // is reset whenever a new file is picked or the file is re-validated.
  const [duplicateDismissed, setDuplicateDismissed] = useState(false);
  const [quotaBlock, setQuotaBlock] = useState<{ used: number; limit: number } | null>(null);
  const [commitReport, setCommitReport] = useState<CommitReport | null>(null);
  // ADR 0034: the commit is async, so the outcome arrives via the jobs poll.
  // submittedJobId tracks the just-fired single-file job; handledJobId guards
  // the once-only outcome handler; reportJobId drives the on-demand /report
  // fetch; batchSubmittedCount drives the one-shot multi-file "N submitted" note.
  const [submittedJobId, setSubmittedJobId] = useState<string | null>(null);
  const [handledJobId, setHandledJobId] = useState<string | null>(null);
  const [reportJobId, setReportJobId] = useState<string | null>(null);
  const [batchSubmittedCount, setBatchSubmittedCount] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  // Import Batch Management: the recent-import row whose delete dialog is open.
  const [deleteTarget, setDeleteTarget] = useState<{ jobId: string; filename: string } | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const usage = usageQuery.data;
  const recentJobs = importJobsQuery.data?.jobs ?? [];
  const deletedJobs = deletedJobsQuery.data?.jobs ?? [];

  // Restore a soft-deleted batch (shared by the trash button and the Undo toast).
  function restoreBatch(jobId: string): void {
    restoreMutation.mutate(
      { jobId },
      {
        onSuccess: () => showToast("Import restored."),
        onError: (err) =>
          showToast(err instanceof Error ? err.message : "Failed to restore import."),
      },
    );
  }

  // After a delete, bring the undo affordance to the user (operator note): a toast
  // with an Undo action that calls the same restore path as the trash button.
  function onImportDeleted(jobId: string): void {
    showToast("Import deleted.", { action: { label: "Undo", onClick: () => restoreBatch(jobId) } });
  }

  const submittedJob = useMemo(
    () => (submittedJobId === null ? undefined : recentJobs.find((j) => j.job_id === submittedJobId)),
    [recentJobs, submittedJobId],
  );
  const reportQuery = useImportJobReport(reportJobId);

  const accounts = useMemo<AccountResponse[]>(
    () => (accountsQuery.data?.items ?? []).filter((a) => !a.is_deleted),
    [accountsQuery.data],
  );

  // L0-U25: resolve a duplicate's prior account_id to a display name. Built from
  // ALL accounts (including soft-deleted) so a prior import into a since-renamed
  // or deleted account still resolves; tenant-wide matches can reference either.
  const accountNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const account of accountsQuery.data?.items ?? []) {
      map.set(account.account_id, account.name);
    }
    return map;
  }, [accountsQuery.data]);

  // Zero-accounts safety net (L0-U10, ADR 0031): imports need an account to
  // land in. A fresh tenant lands here with none and would otherwise face a
  // dead picker, so surface an inline create-account prompt. This keeps the
  // account-before-import fix structural even when the first-run wizard is
  // skipped. Only act on a settled query so the prompt never flashes mid-load.
  const hasNoAccounts = accountsQuery.isSuccess && accounts.length === 0;

  // Account default (L0-U14 item 2): preselect when there is no ambiguity --
  // the sole account, or a remembered last-used account that still exists.
  // More than one account with no remembered choice still forces an explicit
  // pick. Only fills an empty selection, so a manual choice is never overridden.
  useEffect(() => {
    if (accountId !== "" || accounts.length === 0) return;
    const [soleAccount] = accounts;
    if (accounts.length === 1 && soleAccount !== undefined) {
      setAccountId(soleAccount.account_id);
      return;
    }
    const remembered = readLastImportAccount();
    if (remembered !== null && accounts.some((a) => a.account_id === remembered)) {
      setAccountId(remembered);
    }
  }, [accounts, accountId]);

  // ADR 0034: when the tracked single-file job finishes, surface its outcome.
  // A clean success shows the banner; a partial (withheld symbols) or blocked
  // import additionally fetches the /report detail for the inline alert.
  useEffect(() => {
    if (submittedJob === undefined || submittedJobId === null) return;
    if (submittedJobId === handledJobId || isImportJobActive(submittedJob)) return;
    setHandledJobId(submittedJobId);
    const withholds =
      (submittedJob.quarantined_count ?? 0) > 0 || (submittedJob.unreconciled_count ?? 0) > 0;
    if (submittedJob.status === "succeeded") {
      banner.show({
        fillsImported: submittedJob.fills_imported ?? 0,
        tradeCount: submittedJob.trades_created ?? 0,
        newInstrumentCount: submittedJob.new_instruments ?? 0,
      });
    }
    if (submittedJob.blocked || withholds) {
      setReportJobId(submittedJobId);
    } else {
      setSubmittedJobId(null);
    }
  }, [submittedJob, submittedJobId, handledJobId, banner]);

  // Turn the fetched /report detail into the inline blocked/quarantine alert.
  useEffect(() => {
    const report = reportQuery.data;
    if (report === undefined) return;
    // A file-level guided refusal raised DURING the background commit -- an
    // account-mask mismatch the synchronous pre-check passed, say. It arrives
    // here rather than as a 400 because the commit was already accepted, and it
    // must render through the SAME remedy catalogue: the blocked-rowErrors
    // alert below would otherwise say "0 row(s) couldn't be read", which is
    // both wrong and unactionable for a condition that has a specific fix.
    const reportDiagnostics = parseImportDiagnostics({ diagnostics: report.diagnostics });
    if (reportDiagnostics.length > 0) {
      setDiagnostics(reportDiagnostics);
      setSubmittedJobId(null);
      return;
    }
    if (report.blocked) {
      setCommitReport({ kind: "blocked", rowErrors: report.row_errors });
    } else if (report.quarantined_symbols.length > 0 || report.unreconciled_symbols.length > 0) {
      setCommitReport({
        kind: "quarantine",
        symbols: report.quarantined_symbols,
        unreconciled: report.unreconciled_symbols,
        rowErrors: report.row_errors,
      });
    }
    setSubmittedJobId(null);
  }, [reportQuery.data]);

  const isMulti = files.length > 1;
  const singleFile: File | null = files.length === 1 ? (files[0] ?? null) : null;

  // ADR 0039 D9.2. `statement_timezone` is nullable on the wire and its NULL is
  // the state that matters: an import is refused while it is null. Reading it
  // here is what lets the picker be offered BEFORE a refusal, and lets a zone be
  // changed afterwards -- the /validate 400 only happens while no zone exists,
  // so a page that depended on it had no way to honour its own "you can change
  // this until you import your first statement" copy.
  //
  // It does NOT say whether imported fills exist, so it cannot tell a changeable
  // zone from a locked one. That stays the server's answer: the 409 from the PUT
  // names the zone in force, and "are there imported fills" is only correct
  // under a locked read (D9.9).
  const accountZone =
    accounts.find((account) => account.account_id === accountId)?.statement_timezone ?? null;

  // THE UP-FRONT OFFER. Once the file is in hand and the chosen account has no
  // zone, the refusal is already known -- so ask the question now instead of
  // spending a round trip to be told. The /validate 400 path stays as the
  // fallback: a zone can disappear from under this page (another tab, another
  // device) between the accounts query and the upload, and the server is the
  // boundary that actually decides.
  useEffect(() => {
    if (singleFile === null || accountId === "" || accountZone !== null) return;
    const key = [
      accountId,
      singleFile.name,
      singleFile.size,
      singleFile.lastModified,
    ].join(":");
    if (sampleRequestedForRef.current === key) return;
    sampleRequestedForRef.current = key;
    void fetchStatementSample(singleFile);
    // `fetchStatementSample` is intentionally not a dependency: it is recreated
    // on every render and reads only `broker` (listed) plus stable setters, so
    // depending on it would re-run this effect every render and leave the ref
    // guard doing all the work.
  }, [singleFile, accountId, accountZone, broker]);

  const duplicateCount = useMemo(
    () => (preview?.fills ?? []).filter((row) => row.is_duplicate).length,
    [preview],
  );
  const newCount = (preview?.fills.length ?? 0) - duplicateCount;

  // L0-U25 duplicate advisory derived state. priorAccountName falls back to a
  // short id when the prior account can't be resolved; accountMismatch is true
  // only once an account is selected and it differs from the prior import's.
  const duplicateOf: DuplicateImportRef | null = preview?.duplicate_of ?? null;
  const priorAccountName =
    duplicateOf === null
      ? ""
      : (accountNameById.get(duplicateOf.account_id) ??
        `account ${duplicateOf.account_id.slice(0, 8)}`);
  const accountMismatch =
    duplicateOf !== null && accountId !== "" && duplicateOf.account_id !== accountId;

  // ADR 0039 D9.6 -- the stale-id guard's validate-time finding, at `warn`
  // severity. Routed through `parseImportDiagnostics` rather than read field by
  // field so there is ONE parser for the diagnostic shape; it also means a
  // malformed body degrades to null instead of breaking the preview.
  //
  // ADVISORY. Commit re-runs the same guard under the account row lock and is
  // what decides, so this can be stale by the time the user commits -- a
  // re-localisation landing in between moves the answer either way. It is shown
  // because learning here beats learning from a failed background job.
  const staleStatement = useMemo<ImportDiagnostic | null>(() => {
    const raw = preview?.stale_statement ?? null;
    if (raw === null) return null;
    return parseImportDiagnostics({ diagnostics: [raw] })[0] ?? null;
  }, [preview]);

  function resetZonePickerState() {
    setStatementSample(null);
    setZoneConflict(null);
    setZoneError(null);
  }

  // The up-front offer is keyed on (account, file), so a NEW selection has to be
  // allowed to ask again -- including re-picking the same file after a cancel.
  function forgetSampleRequest() {
    sampleRequestedForRef.current = null;
  }

  function resetPreviewState() {
    setPreview(null);
    setDuplicateDismissed(false);
    setDiagnostics([]);
    resetZonePickerState();
  }

  function onFilesPicked(picked: FileList | null) {
    setFiles(picked === null ? [] : Array.from(picked));
    forgetSampleRequest();
    resetPreviewState();
    setBatchSubmittedCount(null);
    setCommitReport(null);
  }

  // --- account-mask re-link (the mismatch remedy) --------------------------
  //
  // Detection without recovery turns a recoverable mistake into a permanent
  // block whose only exit is support: a mis-filed FIRST import leaves the
  // account bound to the wrong mask and then refuses every correct statement.
  //
  // The new mask is derived server-side from the statement, so the file has to
  // be in hand. It usually is -- the user just tried to import it -- but a
  // mismatch read back after a reload or a navigation has no file, so the
  // handler asks for one rather than failing silently.
  async function handleRebindAccountMask() {
    const mismatch = diagnostics.find((d) => d.remedy === REMEDY_REBIND_ACCOUNT_MASK);
    if (mismatch === undefined || accountId === "") return;
    const boundMask = mismatch.extra[0];
    if (boundMask === undefined) {
      showToast("Cannot re-link: the current link is unknown. Validate the file again.");
      return;
    }
    if (singleFile === null) {
      // The browser no longer holds the statement.
      showToast("Re-select the statement file, then re-link.");
      rebindFileInputRef.current?.click();
      return;
    }
    await submitRebind(singleFile, boundMask);
  }

  async function submitRebind(file: File, expectedCurrentMask: string) {
    try {
      const result = await rebindMutation.mutateAsync({
        accountId,
        expectedCurrentMask,
        file,
      });
      setDiagnostics([]);
      showToast(
        result.changed
          ? `Account re-linked to ${result.new_mask}. Import the statement again.`
          : `Account is already linked to ${result.new_mask}.`,
      );
    } catch (err) {
      if (err instanceof AccountMaskConflictError) {
        // Someone changed the link underneath. Re-reading is the only useful
        // response, so the stale diagnostic is cleared rather than left to
        // invite a second overwrite.
        setDiagnostics([]);
        showToast(`${err.message} Validate the file again to see the current link.`);
        return;
      }
      if (err instanceof ImportDiagnosticsError) {
        setDiagnostics(err.diagnostics);
        return;
      }
      showToast(err instanceof Error ? err.message : "Failed to re-link this account.");
    }
  }

  function onRebindFilePicked(picked: FileList | null) {
    const file = picked?.[0];
    const mismatch = diagnostics.find((d) => d.remedy === REMEDY_REBIND_ACCOUNT_MASK);
    const boundMask = mismatch?.extra[0];
    if (file === undefined || boundMask === undefined) return;
    void submitRebind(file, boundMask);
  }

  // --- the statement-timezone picker (ADR 0039 D9.2) -----------------------
  //
  // THE TRIGGER IS THE REFUSAL, not a field on the accounts list: nothing the
  // client already holds says whether an account has a zone, and /validate
  // resolves the zone BEFORE it parses, so a zone-less account refuses with
  // `set_statement_timezone` without ever reading the file. That refusal is the
  // moment the client learns, and the statement is still in hand.
  //
  // THE SAMPLE COMES FROM ITS OWN ENDPOINT, never from the diagnostic. A
  // diagnostic is logged and may end up in an error body; the times a statement
  // prints are real user data and may not go anywhere near one. That separation
  // is the entire reason POST /imports/statement-sample exists.
  async function fetchStatementSample(file: File) {
    try {
      setStatementSample(await sampleMutation.mutateAsync({ file, broker }));
    } catch (err) {
      // The guided refusal is already on screen and still actionable without a
      // sample, so this degrades to a toast rather than replacing it.
      showToast(err instanceof Error ? err.message : "Failed to read times from this statement.");
    }
  }

  function handleDiagnostics(raised: ImportDiagnostic[], file: File | null) {
    setDiagnostics(raised);
    const needsZone = raised.some((d) => d.remedy === REMEDY_SET_STATEMENT_TIMEZONE);
    if (!needsZone || file === null) {
      // No picker when the browser no longer holds the statement -- read back
      // from a finished job, say. There would be nothing to sample, and a zone
      // chosen with no times to check it against is the guess this screen exists
      // to prevent.
      resetZonePickerState();
      return;
    }
    setZoneConflict(null);
    setZoneError(null);
    void fetchStatementSample(file);
  }

  async function handleSetStatementTimezone(timezone: string) {
    if (timezone === "" || accountId === "") return;
    setZoneError(null);
    try {
      const result = await setTimezoneMutation.mutateAsync({ accountId, timezone });
      resetZonePickerState();
      setDiagnostics([]);
      // `was_set_by_this_request` separates a write from a no-op: re-submitting
      // the zone already in force writes nothing and no audit entry, so telling
      // the user it was "set" would overstate what happened.
      showToast(
        result.was_set_by_this_request
          ? `Statement timezone set to ${result.statement_timezone}.`
          : `This account already reads statements as ${result.statement_timezone}.`,
      );
      // The refused validate is what put the picker on screen, so there is no
      // preview to proceed from. Re-run it now the zone exists.
      await handleValidate();
    } catch (err) {
      if (err instanceof StatementTimezoneAlreadySetError) {
        // The import won the race. Read-only from here, naming the zone the
        // RESPONSE carried -- a re-fetch would race the same way.
        setZoneConflict({ inForce: err.inForce, requested: err.requested });
        return;
      }
      if (err instanceof ImportDiagnosticsError) {
        // A LEGACY account (imported fills, no zone) or lock contention. Both
        // render through the same remedy catalogue the import path uses, and
        // neither leaves a picker the user could usefully submit.
        setDiagnostics(err.diagnostics);
        resetZonePickerState();
        return;
      }
      setZoneError(
        err instanceof Error ? err.message : "Failed to set the statement timezone.",
      );
    }
  }

  // THE WAY BACK IN. Without this the "you can change this until you import
  // your first statement" copy is unhonourable: the picker's only other route
  // is the /validate 400, which stops happening the moment a zone exists.
  //
  // It needs the statement in hand, which it has -- the preview this control
  // sits on was built from it -- because the times are what make a zone
  // checkable rather than a guess.
  function handleChangeStatementTimezone() {
    if (singleFile === null) return;
    setZoneConflict(null);
    setZoneError(null);
    void fetchStatementSample(singleFile);
  }

  function handleChooseDifferentFile() {
    resetPreviewState();
    clearFileSelection();
    fileInputRef.current?.click();
  }

  async function handleValidate() {
    if (singleFile === null) return;
    setDiagnostics([]);
    resetZonePickerState();
    try {
      // accountId rides along so the preview can show the account-mask
      // binding (and refuse a statement for a different broker account)
      // before the user commits -- a mis-filed FIRST import is otherwise
      // silently canonical, since an unbound account adopts whatever arrives.
      const result = await validateMutation.mutateAsync({
        file: singleFile,
        broker,
        accountId,
      });
      setPreview(result);
      setDuplicateDismissed(false);
    } catch (err) {
      // A diagnostic is a blocking state the user resolves in another
      // application, so it renders inline and persists. A toast would vanish
      // while they are still reading the procedure.
      if (err instanceof ImportDiagnosticsError) {
        handleDiagnostics(err.diagnostics, singleFile);
        return;
      }
      showToast(err instanceof Error ? err.message : "Failed to validate import.");
    }
  }

  // Clear the file selection after a commit so the operator can pick another CSV
  // without leaving the page (the file input must be reset explicitly).
  function clearFileSelection() {
    setPreview(null);
    setFiles([]);
    // The picker reads a sample of THIS file, so it cannot outlive the
    // selection -- it would be offering a zone against times from a file the
    // browser no longer holds.
    resetZonePickerState();
    forgetSampleRequest();
    if (fileInputRef.current !== null) {
      fileInputRef.current.value = "";
    }
  }

  // ADR 0034: the commit runs in the background. Fire it, capture the job id to
  // track, and clear the file picker -- the outcome (success banner / blocked /
  // quarantine alert) is surfaced by the job-watch effects above once the poll
  // shows the job finished. A synchronous quota block (402) still returns inline.
  async function handleCommit() {
    if (singleFile === null || preview === null) return;
    if (accountId === "") {
      showToast("Pick an account before importing.");
      return;
    }
    try {
      const { job_id } = await commitMutation.mutateAsync({
        file: singleFile,
        accountId,
        broker,
      });
      setQuotaBlock(null);
      setCommitReport(null);
      writeLastImportAccount(accountId);
      setHandledJobId(null);
      setReportJobId(null);
      setSubmittedJobId(job_id);
      clearFileSelection();
    } catch (err) {
      if (err instanceof ImportQuotaExceededError) {
        setQuotaBlock({ used: err.used, limit: err.limit });
        return;
      }
      showToast(err instanceof Error ? err.message : "Failed to commit import.");
    }
  }

  // Multi-file import (L0-U14 item 3, ADR 0034): fire each file's background
  // commit, then show a one-shot "N submitted" note. Per-file progress and
  // outcomes are surfaced by the global pill + the job-backed Recent-imports
  // list (the source of truth), so there is no inline per-file results block. A
  // synchronous reject (e.g. 402 quota) is toasted; the rest still submit. The
  // button is disabled while firing so a double-click can't enqueue 2N jobs.
  async function handleImportAll() {
    if (files.length === 0) return;
    if (accountId === "") {
      showToast("Pick an account before importing.");
      return;
    }
    setSubmitting(true);
    setBatchSubmittedCount(null);
    setCommitReport(null);
    let submitted = 0;
    try {
      for (const file of files) {
        try {
          await commitMutation.mutateAsync({ file, accountId, broker });
          submitted += 1;
        } catch (err) {
          showToast(`${file.name}: ${describeCommitError(err)}`);
        }
      }
    } finally {
      setSubmitting(false);
    }
    if (submitted > 0) {
      writeLastImportAccount(accountId);
      setBatchSubmittedCount(submitted);
      clearFileSelection();
    }
  }

  const zonePicker =
    statementSample === null ? null : (
      <StatementTimezonePicker
        sample={statementSample}
        conflict={zoneConflict}
        error={zoneError}
        currentTimezone={accountZone}
        submitting={setTimezoneMutation.isPending || validateMutation.isPending}
        onSubmit={(timezone) => void handleSetStatementTimezone(timezone)}
        onChooseDifferentFile={handleChooseDifferentFile}
        // Cancel only where there is a previous state to return to. With no zone
        // the import is blocked either way, so a dismiss would just hide the one
        // thing that unblocks it.
        onCancel={accountZone === null ? undefined : resetZonePickerState}
      />
    );

  function renderAccountSelect() {
    return (
      <label className="flex flex-col gap-1 text-sm text-on-surface">
        Account
        <select
          className={`${SELECT_CLASS} min-w-[14rem]`}
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          aria-label="Account"
        >
          <option value="" disabled>
            Pick an account…
          </option>
          {accounts.map((account) => (
            <option key={account.account_id} value={account.account_id}>
              {account.name}
            </option>
          ))}
        </select>
      </label>
    );
  }

  return (
    <section className="space-y-6 p-6">
      <header>
        <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
          Import broker statement
        </h1>
        <p className="mt-1 text-sm text-on-surface-variant">
          Upload a CSV, review the parsed rows, then commit. Unknown symbols become new instruments
          automatically; duplicate fills (by external id) are silently skipped at commit time.
        </p>
        {usage !== undefined && !usage.exempt && (
          <p className="mt-2 text-xs text-on-surface-variant" data-testid="import-usage">
            {usage.used} of {usage.limit} imports used this month.
          </p>
        )}
      </header>

      {hasNoAccounts && (
        <div
          data-testid="no-accounts-prompt"
          className="flex flex-wrap items-start justify-between gap-3 rounded-control border border-outline bg-surface-container p-4 shadow-sm"
        >
          <div className="space-y-1">
            <h2 className="text-sm font-medium text-on-surface">Create an account first</h2>
            <p className="text-sm text-on-surface-variant">
              Imports need an account to land in. Create one to start importing your trades.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => {
              setAccountDialogOpen(true);
            }}
          >
            Create an account
          </Button>
        </div>
      )}

      {quotaBlock !== null && (
        <div
          role="alert"
          className="rounded-control border border-warning bg-warning/10 p-4 text-sm text-on-surface"
        >
          <strong>Monthly import limit reached.</strong>
          <p className="mt-1 text-on-surface-variant">
            You&rsquo;ve used {quotaBlock.used} of {quotaBlock.limit} imports this month. Upgrade
            &mdash; coming soon.
          </p>
        </div>
      )}

      {commitReport !== null && <CommitReportAlert report={commitReport} />}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-6">
          {consentNotGiven ? (
            <StatementConsentGate
              onAgree={() => {
                recordConsent.mutate();
              }}
              pending={recordConsent.isPending}
              failed={recordConsent.isError}
            />
          ) : (
            <>
          <div
            data-testid="import-pitfalls"
            role="note"
            className="rounded-control border border-warning bg-warning/10 p-4 text-sm text-on-surface"
          >
            <strong>Before you upload</strong>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-on-surface-variant">
              <li>
                Export the entire statement, including the Cash Balance section, so fees attach to
                your trades.
              </li>
              <li>Don&rsquo;t rename or reorder the file&rsquo;s column headers.</li>
              <li>
                If a few rows can&rsquo;t be read, we import the rest and tell you which symbols were
                skipped.
              </li>
            </ul>
          </div>

          <div className="space-y-4 rounded-control border border-outline bg-surface-container p-4 shadow-sm">
            <label className="flex max-w-xs flex-col gap-1 text-sm text-on-surface">
              Broker
              <select
                className={SELECT_CLASS}
                value={broker}
                onChange={(e) => {
                  setBroker(e.target.value);
                  resetPreviewState();
                }}
                aria-label="Broker"
              >
                <option value="tos">ThinkOrSwim</option>
              </select>
            </label>

            <FileDropzone
              ariaLabel="CSV file"
              accept=".csv,text/csv"
              multiple
              inputRef={fileInputRef}
              onFilesPicked={onFilesPicked}
              hint="Select one or more CSV files. Multiple files import one after another."
            />

            {isMulti ? (
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex flex-col gap-1 text-sm text-on-surface">
                  <span className="font-medium">{files.length} files selected</span>
                  <span className="text-xs text-on-surface-variant">
                    Each file is committed separately into the account below.
                  </span>
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  {renderAccountSelect()}
                  <Button
                    type="button"
                    onClick={handleImportAll}
                    disabled={submitting || accountId === ""}
                  >
                    {submitting ? "Submitting…" : `Import ${files.length} files`}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={handleValidate}
                  disabled={
                    singleFile === null ||
                    validateMutation.isPending ||
                    // The zone question is open and unanswered, so this can only
                    // come back as the refusal that opened it. Found by looking:
                    // an enabled primary button sitting ABOVE the picker reads as
                    // the next step when it is a dead end. Only while the picker
                    // is actually up -- if the sample fetch failed there is no
                    // picker, and the 400 path must stay reachable.
                    (accountZone === null && statementSample !== null)
                  }
                >
                  {validateMutation.isPending ? "Validating…" : "Validate"}
                </Button>
              </div>
            )}
            <ImportDiagnosticsAlert
              diagnostics={diagnostics}
              onRebindAccountMask={
                accountId === "" || rebindMutation.isPending
                  ? undefined
                  : () => void handleRebindAccountMask()
              }
            />
            {/*
              ONE instance, rendered NEXT TO WHATEVER OPENED IT -- derived from
              whether a preview exists rather than from extra state. Found by
              looking at the real screen: opened from the preview's "Change
              timezone", a picker pinned up here appears far above the control
              that summoned it, and so does the 409 that follows, which on a tall
              page reads as nothing having happened at all.

              No preview yet means the up-front offer or the refusal, both of
              which belong beside the upload controls.
            */}
            {preview === null && zonePicker}
            <input
              ref={rebindFileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              data-testid="rebind-file-input"
              onChange={(event) => onRebindFilePicked(event.target.files)}
            />
          </div>
            </>
          )}

          {batchSubmittedCount !== null && (
            <div
              role="status"
              data-testid="batch-submitted"
              className="rounded-control border border-outline bg-surface-container p-4 text-sm text-on-surface shadow-sm"
            >
              <strong>
                {batchSubmittedCount} file{batchSubmittedCount === 1 ? "" : "s"} submitted.
              </strong>{" "}
              <span className="text-on-surface-variant">
                Each import runs in the background — watch progress in Recent imports below or the
                indicator at the top. It&rsquo;s safe to leave this page.
              </span>
            </div>
          )}

          {!isMulti && preview !== null && (
            <div className="space-y-4 rounded-control border border-outline bg-surface-container p-4 shadow-sm">
              {duplicateOf !== null && !duplicateDismissed && (
                <DuplicateImportWarning
                  duplicate={duplicateOf}
                  priorAccountName={priorAccountName}
                  accountMismatch={accountMismatch}
                  onDismiss={() => setDuplicateDismissed(true)}
                />
              )}
              {/*
                The stale-id warning sits at the TOP of the preview block and is
                NOT dismissible, unlike the duplicate advisory next to it. The
                duplicate advisory warns about something the import handles
                gracefully; this one says the commit will be refused, so a
                dismissed-and-forgotten version would leave the user clicking
                Import into a dead end. Rendered through the same alert component
                as every other diagnostic, which already styles `warn` distinctly
                from `block`.
              */}
              {staleStatement !== null && (
                <ImportDiagnosticsAlert diagnostics={[staleStatement]} />
              )}
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-4 text-sm text-on-surface">
                    <span>
                      <strong>{preview.fill_count}</strong> parsed row(s)
                    </span>
                    <span aria-hidden="true">•</span>
                    <span>
                      <strong>{newCount}</strong> new
                    </span>
                    <span aria-hidden="true">•</span>
                    <span>
                      <strong>{duplicateCount}</strong> duplicate
                    </span>
                    <span aria-hidden="true">•</span>
                    <span>
                      {preview.symbols.length} symbol(s), {preview.account_masks.length} account(s)
                    </span>
                  </div>
                  {preview.earliest_executed_at !== null && (
                    <p className="text-xs text-on-surface-variant">
                      {formatExecutedAt(preview.earliest_executed_at)} →{" "}
                      {formatExecutedAt(preview.latest_executed_at)}
                    </p>
                  )}
                  {/*
                    The zone the times above were computed in. It belongs beside
                    them rather than in settings: every timestamp on this preview
                    is a consequence of it, and a wrong zone is only visible by
                    comparing those times against the statement.
                  */}
                  {accountZone !== null && (
                    <p
                      className="flex flex-wrap items-center gap-2 text-xs text-on-surface-variant"
                      data-testid="statement-timezone-in-force"
                    >
                      <span>
                        Times read as <strong>{accountZone}</strong>.
                      </span>
                      {/*
                        Hidden once the server has said the zone is locked. The
                        outcome table calls that state read-only, and re-offering
                        a control that can only 409 again contradicts the copy
                        sitting directly beneath it.
                      */}
                      {zoneConflict === null && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          data-testid="change-statement-timezone"
                          disabled={sampleMutation.isPending}
                          onClick={handleChangeStatementTimezone}
                        >
                          Change timezone
                        </Button>
                      )}
                    </p>
                  )}
                  {preview !== null && zonePicker}
                </div>
                <div className="flex flex-wrap items-end gap-3">
                  {renderAccountSelect()}
                  <Button
                    type="button"
                    onClick={handleCommit}
                    disabled={
                      commitMutation.isPending ||
                      accountId === "" ||
                      preview.fill_count === 0 ||
                      // Derived from the diagnostics list itself, never from a
                      // separate flag -- one source of truth, or the two drift.
                      hasBlockingDiagnostic(diagnostics)
                    }
                  >
                    {commitMutation.isPending ? "Importing…" : "Confirm import"}
                  </Button>
                </div>
              </div>

              {preview.row_errors.length > 0 && (
                <div
                  role="alert"
                  className="rounded-control border border-warning bg-warning/10 p-3 text-sm text-on-surface"
                >
                  <strong>{preview.row_errors.length} row(s) could not be parsed.</strong>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
                    {preview.row_errors.map((err) => (
                      <li key={`${err.row_number}-${err.message}`}>{err.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {preview.fills.length > 0 && <ParsedRowsTable rows={preview.fills} />}
            </div>
          )}

          {recentJobs.length > 0 && (
            <div
              className="space-y-3 rounded-control border border-outline bg-surface-container p-4 shadow-sm"
              data-testid="recent-imports"
            >
              <h2 className="text-sm font-medium text-on-surface">Recent imports</h2>
              <p className="text-xs text-on-surface-variant">
                Progress is saved on the server, so it&rsquo;s safe to leave this page and come back.
              </p>
              <ul className="space-y-2">
                {recentJobs.map((job) => (
                  <li
                    key={job.job_id}
                    data-testid={`recent-import-${job.status}`}
                    className="flex items-center justify-between gap-4 rounded-control border border-outline-variant px-3 py-2 text-sm"
                  >
                    <span className="truncate font-medium text-on-surface">{job.filename}</span>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className="text-on-surface-variant">{describeJobOutcome(job)}</span>
                      {job.status === "succeeded" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-on-surface-variant hover:text-danger"
                          aria-label={`Delete import ${job.filename}`}
                          onClick={() =>
                            setDeleteTarget({ jobId: job.job_id, filename: job.filename })
                          }
                        >
                          Delete
                        </Button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {deletedJobs.length > 0 && (
            <div
              className="space-y-3 rounded-control border border-outline bg-surface-container p-4 shadow-sm"
              data-testid="deleted-imports"
            >
              <h2 className="text-sm font-medium text-on-surface">
                Deleted imports ({deletedJobs.length})
              </h2>
              <p className="text-xs text-on-surface-variant">
                Restore an import to bring its trades back. After the undo window closes they
                are removed for good.
              </p>
              <ul className="space-y-2">
                {deletedJobs.map((job) => (
                  <li
                    key={job.job_id}
                    data-testid="deleted-import"
                    className="flex items-center justify-between gap-4 rounded-control border border-outline-variant px-3 py-2 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-on-surface">
                        {job.filename}
                      </span>
                      {job.restorable_until !== null && (
                        <span className="text-xs text-on-surface-variant">
                          Restorable until {formatDate(job.restorable_until)}
                        </span>
                      )}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      disabled={restoreMutation.isPending}
                      onClick={() => restoreBatch(job.job_id)}
                    >
                      Restore
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <ImportGuidance />
      </div>

      <AccountFormDialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen} mode="create" />
      <DeleteImportDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
          }
        }}
        jobId={deleteTarget?.jobId ?? null}
        filename={deleteTarget?.filename ?? ""}
        onDeleted={onImportDeleted}
      />
    </section>
  );
}
